import { useEffect, useMemo, useRef, useState } from "react";
import {
  useStore, select, setItemProps, rotateSel, alignToWall, moveToFloor, toggleLock, duplicateSel, deleteSel, placement,
} from "../state/store";
import { COLORS } from "../engine/furniture";

const cmv = (v: number) => Math.round(v * 100);

/** Zahlenfeld, das erst beim Verlassen oder mit Enter übernimmt (wie das change-Ereignis) */
function NumField({ id, label, value, onCommit, disabled, min, max, step = "1", mode = "numeric" }: {
  id: string; label: string; value: number; onCommit: (v: number) => void; disabled?: boolean;
  min?: number; max?: number; step?: string; mode?: "numeric" | "decimal";
}) {
  const [draft, setDraft] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(String(value)); }, [value]);
  const commit = () => { const v = parseFloat(draft); if (isFinite(v)) onCommit(v); else setDraft(String(value)); };
  return (
    <label htmlFor={id}>{label}
      <input id={id} type="number" inputMode={mode} step={step} min={min} max={max} disabled={disabled} value={draft}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; commit(); }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
    </label>
  );
}

export function SelectionPanel() {
  const it = useStore((s) => (s.selectedId ? s.items.find((i) => i.id === s.selectedId) : undefined));
  const items = useStore((s) => s.items);
  const bad = useStore((s) => !!it && s.prefs.coll && s.bad.has(it.id));
  const readOnly = useStore((s) => !s.edit);
  const show = useStore((s) => s.layers.furniture);
  const gaps = useMemo(() => (it ? placement(items).gaps(it) : null), [it, items]);
  if (!it || !show) return null;
  const L = it.lock || readOnly;
  const cm = (q: { d: number } | null) => (q && q.d > -0.005 ? Math.max(0, Math.round(q.d * 100)) + " cm" : "–");
  const deg = Math.round(((((-it.r * 180) / Math.PI) % 360) + 360) % 360);
  return (
    <section className="sheet" id="selpanel" aria-label="Möbelstück bearbeiten">
      <div className="sh-head">
        <h2 id="selname">{it.name}</h2>
        <div className="row">
          <button className="btn small" id="s-lock" aria-pressed={it.lock} title="Fixieren (L)" disabled={readOnly} onClick={() => toggleLock(it.id)}>{it.lock ? "Fixiert" : "Fixieren"}</button>
          <button className="x" id="sel-close" aria-label="Auswahl aufheben" onClick={() => select(null)}>×</button>
        </div>
      </div>
      <div className="sh-body">
        <p className="warn" id="selwarn" hidden={!bad}>Überschneidet sich mit einer Wand oder einem anderen Möbel.</p>
        <div className="dims">
          <NumField id="d-w" label="Breite cm" value={cmv(it.w)} disabled={L} min={1} max={1200} onCommit={(v) => v > 0 && setItemProps({ w: Math.min(12, v / 100) })} />
          <NumField id="d-d" label="Tiefe cm" value={cmv(it.d)} disabled={L} min={1} max={1200} onCommit={(v) => v > 0 && setItemProps({ d: Math.min(12, v / 100) })} />
          <NumField id="d-h" label="Höhe cm" value={cmv(it.h)} disabled={L} min={1} max={600} onCommit={(v) => v > 0 && setItemProps({ h: Math.min(12, v / 100) })} />
        </div>
        <div className="dims dims4">
          <NumField id="p-x" label="X cm" mode="decimal" value={cmv(it.x)} disabled={L} onCommit={(v) => setItemProps({ x: v / 100 })} />
          <NumField id="p-y" label="Y cm" mode="decimal" value={cmv(it.y)} disabled={L} onCommit={(v) => setItemProps({ y: v / 100 })} />
          <NumField id="p-z" label="über Boden" mode="decimal" value={cmv(it.z)} disabled={L} min={0} max={400} onCommit={(v) => setItemProps({ z: Math.max(0, Math.min(4, v / 100)) })} />
          <NumField id="p-r" label="Drehung °" mode="decimal" value={deg} disabled={L} onCommit={(v) => setItemProps({ r: (-v * Math.PI) / 180 })} />
        </div>
        <p className="meta" id="selgaps">{gaps ? `Abstand  ← ${cm(gaps.nx)}   → ${cm(gaps.px)}   ↑ ${cm(gaps.py)}   ↓ ${cm(gaps.ny)}` : ""}</p>
        <div className="row">
          <button className="btn small" id="r-l" disabled={L} onClick={() => rotateSel(Math.PI / 12)}>↺ 15°</button>
          <button className="btn small" id="r-r" disabled={L} onClick={() => rotateSel(-Math.PI / 12)}>↻ 15°</button>
          <button className="btn small" id="r-90" disabled={L} title="R" onClick={() => rotateSel(-Math.PI / 2)}>90° drehen</button>
          <button className="btn small" id="s-wall" disabled={L} title="W" onClick={alignToWall}>An Wand stellen</button>
          <select id="s-floor" disabled={L} aria-label="Geschoss" value={String(it.f)} onChange={(e) => moveToFloor(+e.target.value)}>
            <option value="0">UG</option><option value="1">EG</option><option value="2">OG</option><option value="3">DG</option>
          </select>
        </div>
        <div className="swatches" id="swatches" role="group" aria-label="Farbe">
          <label className="sw custom" title="Eigene Farbe">
            <input id="s-color" type="color" aria-label="Eigene Farbe" disabled={L} value={/^#[0-9a-f]{6}$/i.test(it.c) ? it.c : "#cccccc"} onChange={(e) => setItemProps({ c: e.target.value })} />
          </label>
          {COLORS.map((c) => (
            <button key={c} className="sw" style={{ background: c }} aria-label={"Farbe " + c} aria-pressed={it.c === c} disabled={L} onClick={() => setItemProps({ c })} />
          ))}
        </div>
        {readOnly ? <p className="note">Zum Ändern im Menü den Bearbeiten-Modus einschalten.</p> : (
          <div className="row">
            <button className="btn small" id="s-dup" onClick={duplicateSel}>Duplizieren</button>
            <button className="btn small danger" id="s-del" disabled={L} onClick={deleteSel}>Löschen</button>
          </div>
        )}
      </div>
    </section>
  );
}
